/**
 * TCL Page nav — scrollspy + the page-wide anchor offset.
 *
 * Two jobs, both of which the CSS cannot do on its own:
 *
 * 1. SCROLLSPY. Marks the link whose section is in the reading band with
 *    `aria-current="true"`. IntersectionObserver with the same -30%/-60%
 *    rootMargin option C uses, so the "current" section is the one a reader is
 *    actually looking at rather than whichever one merely touches the viewport.
 *
 * 2. ANCHOR OFFSET. Publishes the nav's measured bottom edge as
 *    `--tl-anchor-offset` on <html>. Dawn's header is `sticky_header_type:
 *    always` and this nav parks beneath it, so an un-offset `#anchor` jump
 *    lands the target heading behind both bars. `assets/tcl-base.css` feeds the
 *    value into `scroll-margin-top` for every `[data-tcl-anchor]`. It is
 *    MEASURED because `--header-height` changes with the logo size, the
 *    announcement bar and the breakpoint — any constant would be wrong on some
 *    viewport.
 *
 * 3. DEAD-ANCHOR PRUNE — a SAFETY NET, not the primary mechanism. Hides any link
 *    whose target section is not on the page, and hides the whole nav when fewer
 *    than two links survive.
 *
 *    The primary mechanism is server-side: a Link block may name the field its
 *    target section gates on (`require_field`), and `sections/tcl-page-nav.liquid`
 *    drops the link before it is ever rendered. That is strictly better than
 *    doing it here, and an earlier version of this file argued the opposite on
 *    the grounds that "a section cannot enumerate its siblings in Liquid" — true
 *    but irrelevant, because the nav does not need to inspect its siblings, only
 *    to re-evaluate the same predicate they do, from the same treatment object.
 *    Pruning here alone meant a flash of dead links before the deferred script
 *    ran, a layout shift when they vanished, links a keyboard user could focus
 *    in that window, nothing at all with JS off, and — worst — the floating
 *    site-wide TOC standing down for a nav that had erased itself, leaving a
 *    page with no navigation at all.
 *
 *    What remains here covers what the server cannot know: a hand-authored nav
 *    whose `target` is a typo, or a section removed by an app. Numbers are
 *    re-derived after the prune — they are server-rendered `01`/`02`/`03`, so
 *    dropping the middle of the run would otherwise leave the rail reading
 *    `02  05  07`.
 *
 *    It STANDS DOWN IN THE THEME EDITOR, where the section renders its own
 *    "these links have no target" panel and a nav that silently emptied itself
 *    mid-edit would fight it.
 *
 * PROGRESSIVE ENHANCEMENT
 * -----------------------
 * With no JS the nav is still a working list of in-page links, sticky via CSS,
 * and `tcl-base.css` falls back to a header-height-only offset. Nothing here
 * gates content or interaction; it only sharpens both. Because the server now
 * drops collapsed links, the no-JS case is correct on treatment pages too.
 */

(function () {
  'use strict';

  if (window.__tclPageNavLoaded) return;
  window.__tclPageNavLoaded = true;

  var instances = [];
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  function mount(nav) {
    if (nav.dataset.pagenavOn === 'true') return;

    var links = Array.prototype.slice.call(nav.querySelectorAll('[data-pagenav-link]'));
    if (!links.length) return;

    nav.dataset.pagenavOn = 'true';

    var inst = { nav: nav, links: links, io: null, ro: null, onResize: null };

    /* ---- dead-anchor prune -----------------------------------------------
       Returns the number of links whose target is actually on the page. Safe to
       call repeatedly: it sets and clears `hidden` rather than removing nodes,
       and renumbering is derived from scratch each pass, so running it twice
       yields the same DOM as running it once. */
    function liOf(link) {
      /* Scoped to the nav's own list: an unbounded closest('li') would climb out
         of this section if the markup ever changed. Falls back to the link
         itself so a dead link is never left visible merely because no <li> was
         found — the earlier version guarded `if (li)` and silently skipped. */
      return (link.closest && link.closest('.tl-pagenav__list > li')) || link;
    }

    function prune() {
      if (designMode) return links.length;

      var survivors = 0;
      links.forEach(function (link) {
        var href = link.getAttribute('href') || '';
        var alive =
          href.charAt(0) === '#' &&
          href.length > 1 &&
          !!document.getElementById(decodeURIComponent(href.slice(1)));
        var li = liOf(link);
        if (alive) li.removeAttribute('hidden');
        else li.setAttribute('hidden', '');
        if (alive) survivors++;
      });

      /* Renumber what is left. The numbers are decorative (`aria-hidden`), but a
         rail reading `02  05  07` reads as broken. */
      var n = 0;
      links.forEach(function (link) {
        var li = liOf(link);
        if (li.hasAttribute('hidden')) return;
        n++;
        var num = link.querySelector('.tl-pagenav__num');
        if (num) num.textContent = ('0' + n).slice(-2);
      });

      /* THIS DOES NOT HIDE THE NAV, and that is deliberate.
         `sections/tcl-page-nav.liquid` owns the below-two case server-side, where
         it can omit the element entirely. Hiding it here instead would leave
         `data-tcl-pagenav` in the DOM — and `assets/table-of-contents.js` removes
         the site-wide floating TOC whenever that attribute matches, before
         registering its own listeners, so nothing brings it back. That is how an
         earlier version of this file left pages with NO navigation at all. On a
         page the server did not gate (`page.about.json` has seven links and no
         `require_field`s), hiding here would reintroduce exactly that. Pruning
         individual links is safe because the nav itself stays present. */
      return survivors;
    }

    /* Prune BEFORE measuring, so the offset reflects the nav's real height. The
       return value is deliberately not used to gate anything: if the server
       rendered this nav then the bar IS on the page and its offset must be
       published, however few links survived. The server decides whether a nav
       exists; this only decides which of its links are usable. */
    prune();

    /* ---- anchor offset ---------------------------------------------------
       The stuck bottom edge is `sticky top + height`. The live
       `getBoundingClientRect().bottom` is only that value WHILE stuck — before
       the nav reaches the top of the viewport the rect is wherever it sits in
       the document, which would be far too large. So the sticky `top` is read
       from computed style and the height measured.

       The element that actually sticks is Shopify's section wrapper, not the
       <nav>: a sticky element can only travel inside its parent's box, and the
       wrapper is sized to exactly the nav's height, so sticking the nav gives
       it zero travel. `stickyEl()` resolves whichever ancestor carries
       `position: sticky`, so this keeps working if the nav is ever rendered
       outside a section wrapper (a snippet include, the design-mode preview). */
    function stickyEl() {
      var el = nav;
      while (el && el !== document.body) {
        if (window.getComputedStyle(el).position === 'sticky') return el;
        el = el.parentElement;
      }
      return nav;
    }

    function publishOffset() {
      var target = stickyEl();
      var stickyTop = parseFloat(window.getComputedStyle(target).top);
      if (!isFinite(stickyTop)) stickyTop = 0;
      var height = target.offsetHeight || nav.offsetHeight || 0;
      // +16px of breathing room so a heading does not touch the nav's underside.
      document.documentElement.style.setProperty(
        '--tl-anchor-offset',
        stickyTop + height + 16 + 'px'
      );
    }

    publishOffset();

    if (window.ResizeObserver) {
      inst.ro = new ResizeObserver(publishOffset);
      /* Observe the element that actually sticks, not the inner <nav>: the
         sticky `top` resolves `var(--header-height)`, which the header
         publishes asynchronously, so the wrapper can change offset without the
         nav ever resizing. */
      inst.ro.observe(stickyEl());
    }
    /* The header's own height is published asynchronously by
       sections/header.liquid and changes on resize and on scroll (the
       reduce-logo-size mode), so the nav's sticky `top` is not stable at mount.
       A resize listener catches the breakpoint cases; the ResizeObserver above
       catches the nav's own reflow. */
    inst.onResize = function () {
      publishOffset();
    };
    window.addEventListener('resize', inst.onResize, { passive: true });

    /* ---- scrollspy -------------------------------------------------------- */
    if (!('IntersectionObserver' in window)) {
      instances.push(inst);
      return;
    }

    var byId = {};
    var targets = [];
    links.forEach(function (link) {
      var href = link.getAttribute('href') || '';
      if (href.charAt(0) !== '#' || href.length < 2) return;
      var id = decodeURIComponent(href.slice(1));
      // A link whose target is not on the page must not be able to become
      // current — it would highlight a destination that does not exist.
      var el = document.getElementById(id);
      if (!el) return;
      byId[id] = link;
      targets.push(el);
    });

    if (!targets.length) {
      instances.push(inst);
      return;
    }

    var current = null;

    function setCurrent(link) {
      if (link === current) return;
      if (current) current.removeAttribute('aria-current');
      current = link;
      if (current) current.setAttribute('aria-current', 'true');
    }

    inst.io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var link = byId[entry.target.id];
          if (link) setCurrent(link);
        });
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );

    targets.forEach(function (el) {
      inst.io.observe(el);
    });

    instances.push(inst);
  }

  function destroy(scope) {
    for (var i = instances.length - 1; i >= 0; i--) {
      var inst = instances[i];
      if (scope && !scope.contains(inst.nav)) continue;
      if (inst.io) inst.io.disconnect();
      if (inst.ro) inst.ro.disconnect();
      if (inst.onResize) window.removeEventListener('resize', inst.onResize);
      delete inst.nav.dataset.pagenavOn;
      instances.splice(i, 1);
    }
    // With no nav left there is no measured offset to honour; drop back to the
    // CSS fallback rather than leaving a stale value that no longer matches
    // anything on the page.
    if (!instances.length) {
      document.documentElement.style.removeProperty('--tl-anchor-offset');
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-tcl-pagenav]').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    destroy(e.target);
    init(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    destroy(e.target);
  });
})();
